import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import AnimatedTitle from '../../AnimatedTitle/AnimatedTitle'
import styles from './Advantages.module.css'

const advantages = [
  {
    img: '/images/advantages/1.png',
    icon: '/images/advantages/1-icon.svg',
    title: 'Обсмажуємо самі',
    desc: 'Контролюємо смак від зерна до чашки — щоб знайоме звучання щоразу повертало тобі ясність, без хаосу зайвих ноток',
  },
  {
    img: '/images/advantages/2.png',
    icon: '/images/advantages/2-icon.svg',
    title: 'З тобою — будь-куди',
    desc: 'Смакуй у кавʼярні, бери із собою чи замовляй доставку зерен додому — тільки ти вирішуєш, яким буде твій seaters-момент',
  },
  {
    img: '/images/advantages/3.png',
    icon: '/images/advantages/3-icon.svg',
    title: 'Любимо справжність',
    desc: 'Зосереджений, натхненний, тривожний — приходь будь-яким, ми налаштуємось на твій ритм і приготуємо саме те, що треба зараз',
  },
  {
    img: '/images/advantages/4.png',
    icon: '/images/advantages/4-icon.svg',
    title: 'У нас затишно і в черзі',
    desc: 'Але ми стараємось не змушувати тебе чекати та стрімко скорочуємо дистанцію між тобою і таким необхідним кавовим ритуалом',
  },
]

const Advantages = () => {
  const cardsRef = useRef<HTMLDivElement | null>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const [inViews, setInViews] = useState<boolean[]>(() => Array(advantages.length).fill(false))
  const [isMobile, setIsMobile] = useState(false)
  const progressBarRef = useRef<HTMLDivElement | null>(null)

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    cardRefs.current.forEach((ref, idx) => {
      if (!ref) return
      const observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInViews(prev => {
              if (prev[idx]) return prev // вже true
              const next = [...prev]
              next[idx] = true
              return next
            })
            observer.disconnect() // once: true
          }
        },
        { threshold: 0.5 },
      )
      observer.observe(ref)
      observers.push(observer)
    })
    return () => {
      observers.forEach(observer => observer.disconnect())
    }
  }, [])

  const renderCard = (item: any, idx: number) => (
    <motion.div
      ref={el => {
        cardRefs.current[idx] = el
      }}
      className={styles.card}
      key={idx}
      initial={
        isMobile
          ? { opacity: 1, y: 0, rotate: 0, scale: 1 }
          : { opacity: 0, y: 60, rotate: 5, scale: 0.9 }
      }
      animate={
        isMobile
          ? { opacity: 1, y: 0, rotate: 0, scale: 1 }
          : inViews[idx]
          ? { opacity: 1, y: 0, rotate: 0, scale: 1 }
          : false
      }
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div
        className={styles.imageWrap}
        style={{
          WebkitMaskImage: `url('/images/Union.svg')`,
          maskImage: `url('/images/Union.svg')`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
        }}
      >
        <img
          src={item.img}
          alt={item.title}
          className={styles.image}
          width={886}
          height={945}
          loading="lazy"
        />
      </div>
      <div className={styles.info}>
        <div className={styles.iconWrap}>
          <img src={item.icon} alt="" width={21} height={20} loading="lazy" />
        </div>
        <h3 className={styles.cardTitle}>{item.title}</h3>
        <p className={styles.cardDesc}>{item.desc}</p>
      </div>
    </motion.div>
  )

  return (
    <section id="advantages" className={styles.advantagesSection}>
      <AnimatedTitle as="h2" text={['Що робить нас особливими?']} className={styles.title} />

      {isMobile ? (
        <>
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={20}
            slidesPerView={1.1}
            centeredSlides={false}
            onSlideChange={(swiper: SwiperType) => {
              if (progressBarRef.current) {
                const progress = (swiper.activeIndex + 1) / swiper.slides.length
                progressBarRef.current.style.width = `${progress * 100}%`
              }
            }}
            className={styles.swiper}
          >
            {advantages.map((item, idx) => (
              <SwiperSlide key={idx}>{renderCard(item, idx)}</SwiperSlide>
            ))}
          </Swiper>

          <div className={styles.progressWrap}>
            <div className={styles.progressBar}>
              <div
                ref={progressBarRef}
                className={styles.progressBarFill}
                style={{ width: `${100 / advantages.length}%` }}
              />
            </div>
          </div>
        </>
      ) : (
        <div className={styles.cards} ref={cardsRef}>
          {advantages.map((item, idx) => renderCard(item, idx))}
        </div>
      )}
    </section>
  )
}

export default Advantages
